import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { COMPANY_URL } from "@/lib/brand";

type RosterEntry = { display_name: string; avatar_path: string | null; handle: string | null; last_shift: string };
type Microsite = {
  handle: string;
  name: string | null;
  headline: string | null;
  about_me: string | null;
  avatar_path: string | null;
  is_verified: boolean;
  roster: RosterEntry[];
  reviews: { avg: number | null; count: number | null };
  badge: string;
};

export const Route = createFileRoute("/v/$handle")({
  loader: async ({ params }) => {
    const { data } = await supabase.rpc("get_public_venue_microsite", { _handle: params.handle });
    if (!data) throw notFound();
    return { site: data as unknown as Microsite };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.site;
    const title = s ? `${s.name ?? s.handle} — Staffed by The Hospitality Reserve` : "Venue";
    const desc = s?.headline ?? `${s?.name ?? "This venue"} works with vetted bartenders through The Hospitality Reserve.`;
    const url = `${COMPANY_URL}/v/${s?.handle ?? ""}`;
    return {
      meta: [
        { title }, { name: "description", content: desc },
        { property: "og:title", content: title }, { property: "og:description", content: desc },
        { property: "og:url", content: url }, { property: "og:type", content: "website" },
        { property: "og:site_name", content: "The Hospitality Reserve" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title }, { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },

  component: VenueMicrosite,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center"><p>Venue not found.</p>
        <Link to="/" className="underline text-accent">Home</Link></div>
    </div>
  ),
});

function VenueMicrosite() {
  const { site } = Route.useLoaderData() as { site: Microsite };
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: site.name,
    description: site.headline ?? undefined,
    aggregateRating: site.reviews.count ? {
      "@type": "AggregateRating", ratingValue: site.reviews.avg, reviewCount: site.reviews.count,
    } : undefined,
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }} />
      <header className="px-6 py-5 border-b border-border flex items-center justify-between">
        <Link to="/" className="font-display">The Hospitality Reserve</Link>
        <Link to="/auth" className="text-xs tracking-wide-luxury text-accent">Apply to staff venues</Link>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl">{site.name ?? site.handle}</h1>
            {site.headline && <p className="text-muted-foreground mt-1">{site.headline}</p>}
          </div>
          <div className="flex items-center gap-2 border border-accent/40 px-3 py-2 rounded-full bg-accent/5">
            <span className="h-2 w-2 rounded-full bg-accent" />
            <span className="text-xs tracking-wide-luxury text-accent">{site.badge}</span>
          </div>
        </div>

        {site.about_me && (
          <p className="mt-6 leading-relaxed whitespace-pre-wrap text-muted-foreground">{site.about_me}</p>
        )}

        <div className="mt-10">
          <h2 className="text-sm tracking-wide-luxury text-muted-foreground mb-4">Recent roster</h2>
          {site.roster.length === 0 ? (
            <p className="text-muted-foreground text-sm">No completed shifts yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {site.roster.map((m, i) => {
                const initials = (m.display_name ?? "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
                const inner = (
                  <div className="border border-border rounded p-4 text-center hover:border-accent transition-colors">
                    <div className="mx-auto h-14 w-14 rounded-full bg-accent/20 text-accent grid place-items-center font-display">{initials}</div>
                    <p className="mt-2 text-sm">{m.display_name}</p>
                    {m.handle && <p className="text-xs text-muted-foreground">@{m.handle}</p>}
                  </div>
                );
                return m.handle ? (
                  <Link key={i} to="/b/$handle" params={{ handle: m.handle }}>{inner}</Link>
                ) : (
                  <div key={i}>{inner}</div>
                );
              })}
            </div>
          )}
        </div>

        {site.reviews.count ? (
          <div className="mt-10 text-sm text-muted-foreground">
            ★ {site.reviews.avg} from {site.reviews.count} bartender review{site.reviews.count === 1 ? "" : "s"}.
          </div>
        ) : null}
      </section>
    </div>
  );
}
